import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/motion";
import CTASection from "@/components/CTASection";
import usePageMeta from "@/hooks/usePageMeta";
import { BRAND, BLOG_POSTS } from "@/data/content";

export default function BlogPost() {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((p) => p.slug === slug);
  const others = BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, 2);

  usePageMeta(
    post ? `${post.title} — ${BRAND.name}` : `Article — ${BRAND.name}`,
    post?.excerpt
  );

  if (!post) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center px-6 text-center" data-testid="blog-not-found">
        <h1 className="font-serif text-4xl text-white">Article not found.</h1>
        <Link to="/blog" data-testid="blog-back-link" className="btn-primary mt-8">
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Back to Blog
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="blog-post-page">
      <article className="mx-auto max-w-3xl px-6 pb-24 pt-40 md:pt-52">
        <Reveal>
          <Link
            to="/blog"
            data-testid="blog-post-back-link"
            className="group inline-flex items-center gap-2 text-sm text-slate-400 transition-colors hover:text-gold"
          >
            <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" strokeWidth={1.5} />
            All Articles
          </Link>
          <p className="kicker mt-10 text-gold">{post.category}</p>
          <h1 className="mt-5 font-serif text-4xl leading-[1.12] tracking-tight text-white md:text-5xl">
            {post.title}
          </h1>
          <div className="mt-6 flex items-center gap-4 text-xs uppercase tracking-[0.2em] text-slate-500">
            <span>{post.date}</span>
            <span className="h-1 w-1 rounded-full bg-slate-600" />
            <span>{post.readTime}</span>
            <span className="h-1 w-1 rounded-full bg-slate-600" />
            <span>By {BRAND.name}</span>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-12 space-y-6 border-t border-[#1E3A5F] pt-12">
            {post.content.map((para, i) => (
              <p
                key={i}
                className={
                  i === 0
                    ? "font-serif text-xl italic leading-relaxed text-slate-200"
                    : "text-base leading-relaxed text-slate-400"
                }
              >
                {para}
              </p>
            ))}
          </div>
        </Reveal>
      </article>

      <section className="mx-auto max-w-3xl px-6 pb-24">
        <Reveal>
          <h2 className="kicker mb-8 text-gold">Keep Reading</h2>
          <div className="grid gap-6 sm:grid-cols-2">
            {others.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                data-testid={`blog-related-${p.slug}`}
                className="group rounded-2xl border border-[#1E3A5F] bg-[#0A2540] p-7 transition-[border-color] duration-300 hover:border-gold/50"
              >
                <p className="kicker !text-[10px] text-gold">{p.category}</p>
                <h3 className="mt-3 font-serif text-lg leading-snug text-white group-hover:text-gold-light">
                  {p.title}
                </h3>
                <span className="mt-4 inline-flex items-center gap-2 text-xs text-slate-500">
                  Read <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
                </span>
              </Link>
            ))}
          </div>
        </Reveal>
      </section>

      <CTASection />
    </div>
  );
}
